import clsx from 'clsx'
import type { ComponentProps, PropsWithChildren, ReactElement } from 'react'
import { forwardRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import Label from './Label/Label'
import ExclusiveLabel from './Label/ExclusiveLabel'

const Container = styled.div`
  display: grid;
  --sheet-border-width: 1px;
  grid-template-columns: [border-start] var(--sheet-border-width) [content-start] minmax(0px, 1fr) [content-end] var(--sheet-border-width) [border-end];
  grid-template-rows:
    [label-start]
    auto
    [border-start]
    var(--sheet-border-width)
    auto
    [label-end content-start]
    minmax(0px, 1fr)
    [content-end]
    var(--sheet-border-width)
    [border-end];
`

const LabelContainer = styled.div`
  grid-column: border;
  grid-row: label;

  &.placement-start { justify-self: start; }
  &.placement-center { justify-self: center; }
  &.placement-end { justify-self: end; }
`

const Border = styled.div`
  grid-column: border;
  grid-row: border;
  pointer-events: none;
  border-width: var(--sheet-border-width);
  border-style: solid;
  border-radius: ${props => props.theme.borderRadius.M};

  &.kind-highlight { border-color: ${props => props.theme.palette.highlight.primary.darkForeground}; }
  &.kind-highlight-secondary { border-color: ${props => props.theme.palette.highlight.secondary.normalBackground}; }
  &.kind-lux-plus { border-color: ${props => props.theme.palette.product.luxPlus.foreground}; }
  &.kind-exclusive { border-color: ${props => props.theme.palette.product.exclusive.foreground}; }
  &.kind-loyalty-bronze { border-color: ${props => props.theme.palette.product.loyalty.bronze.darkBackground}; }
  &.kind-loyalty-silver { border-color: ${props => props.theme.palette.product.loyalty.silver.darkBackground}; }
  &.kind-loyalty-gold { border-color: ${props => props.theme.palette.product.loyalty.gold.darkBackground}; }
  &.kind-loyalty-platinum { border-color: ${props => props.theme.palette.product.loyalty.platinum.darkBackground}; }
  &.kind-loyalty-infinite { border-color: ${props => props.theme.palette.product.loyalty.infinite.darkBackground}; }
`

const Content = styled.div`
  grid-column: content;
  grid-row: content;
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  kind: 'highlight'
    | 'lux-plus'
    | 'highlight-secondary'
    | 'exclusive'
    | 'loyalty-bronze'
    | 'loyalty-silver'
    | 'loyalty-gold'
    | 'loyalty-platinum'
    | 'loyalty-infinite'
  /**
   * If string is provided, it'll be wrapped by the proper label component.
   * For more complex scenarios, manually pass the appropriate label component.
   */
  label: string | ReactElement
  /**
   * @default start
   */
  labelPlacement?: 'start' | 'center' | 'end'
  className?: string
  style?: React.CSSProperties
}

const KINDS_LABEL_VARIANTS: Record<Props['kind'], ComponentProps<typeof Label>['variant']> = {
  highlight: 'highlight-primary-dark',
  'highlight-secondary': 'highlight-secondary',
  'lux-plus': 'lux-plus',
  exclusive: 'exclusive',
  'loyalty-bronze': 'loyalty-bronze-dark',
  'loyalty-silver': 'loyalty-silver-dark',
  'loyalty-gold': 'loyalty-gold-dark',
  'loyalty-platinum': 'loyalty-platinum-dark',
  'loyalty-infinite': 'loyalty-infinite-dark',
}

const LabeledSheet = forwardRef<HTMLDivElement, Props>(function LabeledSheet(
  {
    children,
    kind,
    label,
    labelPlacement = 'start',
    ...rest
  },
  ref,
) {
  const isStringChildren = typeof label === 'string'

  return <Container {...rest} ref={ref}>
    <Border
      aria-hidden="true"
      className={clsx(`kind-${kind}`)}
    />
    <LabelContainer className={clsx(`placement-${labelPlacement}`)}>
      {isStringChildren && kind === 'exclusive' && <ExclusiveLabel>{label}</ExclusiveLabel>}
      {isStringChildren && kind !== 'exclusive' && <Label kind="primary" variant={KINDS_LABEL_VARIANTS[kind]}>{label}</Label>}
      {!isStringChildren && label}
    </LabelContainer>
    <Content>{children}</Content>
  </Container>
})

export default LabeledSheet
